/**
 * Models for the Edge Live Monitor internal tool (`/internal/tools/edge-monitor`).
 *
 * These mirror the gateway WebAPI DTOs (see `GoToStockServer-connectivity` →
 * `MachineGateway/.../Areas/Customer/Models/MachineStatusDto.cs` and the
 * `Areas/Admin/Models/SiteAgentDto.cs` + `MachineConnectionDto.cs`). Defined
 * locally rather than imported from a shared models barrel because the
 * `Signals` field on `MachineStatus` is a forward-declared addition that the
 * Connectivity Backend Dev is implementing in parallel (§13.2 of
 * EDGE_LIVE_MONITOR_SPEC.md). Until that lands, `Signals` will be undefined at
 * runtime and the page renders the 11 normalized fields only — the remaining
 * tiles render as stale per §3.2.3.
 */

export enum MachineStatusCode {
  Running = 1,
  Idle = 2,
  Stopped = 3,
  Alarm = 4,
  Offline = 5,
}

export enum MachineEventType {
  StatusChanged = 1,
  AlarmRaised = 2,
  AlarmCleared = 3,
  ProgramStarted = 4,
  ProgramEnded = 5,
  PartCompleted = 6,
  AgentConnected = 7,
  AgentDisconnected = 8,
}

/**
 * Mirrors `MachineStatusDto` on the gateway WebAPI. The `signals` field is
 * forward-declared per §13.2 — until the backend extension lands, it will be
 * undefined and the page falls back to the 11 normalized fields only.
 */
export interface MachineStatusDto {
  machineId: string;
  status: MachineStatusCode;
  program?: string | null;
  spindleSpeed?: number | null;
  feedRate?: number | null;
  spindleLoad?: number | null;
  partCount?: number | null;
  alarmCode?: string | null;
  alarmMessage?: string | null;
  uptime?: number | null;
  updatedAt: string; // ISO timestamp
  agentTimestamp: string; // ISO timestamp
  /**
   * §13.2 forward-declared: backend ships `Dictionary<string, JsonElement>`
   * which serializes to `Record<string, unknown>`. Keys mirror `s7DbMap`
   * verbatim, values are post-invert booleans for bool tags or the raw
   * numeric for REAL/INT/DWORD tags. May be undefined until §13 lands.
   */
  signals?: Record<string, unknown>;
}

export interface MachineEventDto {
  id: number;
  machineId: string;
  eventType: MachineEventType;
  previousStatus?: number | null;
  newStatus?: number | null;
  alarmCode?: string | null;
  alarmMessage?: string | null;
  programName?: string | null;
  details?: string | null;
  timestamp: string; // ISO timestamp
}

export interface SiteAgentDto {
  id: string;
  name: string;
  apiKeyPrefix: string;
  lastHeartbeat?: string | null;
  version?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export enum ProtocolType {
  Sharp7 = 1,
  Mtconnect = 2,
  OpcUa = 3,
}

export interface MachineConnectionDto {
  id: number;
  machineId: string;
  siteAgentId: string;
  protocolType: ProtocolType;
  connectionString: string;
  pollingIntervalSeconds: number;
  isEnabled: boolean;
  lastDataReceived?: string | null;
}

/** Page-local: connection state pill in Zone 1. */
export type SignalRConnState = "live" | "reconnecting" | "polling" | "offline";

/**
 * Mirrors `GatewayMachineSummaryDto` on the gateway WebAPI (commit
 * `b8a610da` on `module/connectivity`). Returned by
 * `GET /v1/gateway/machines` (sorted server-side: agentOnline desc,
 * lastDataReceived desc, name asc; permission `Machinery:Read`,
 * customer-scoped). Drives the Zone 1 machine picker dropdown.
 */
export interface GatewayMachineSummary {
  /** Guid string */
  machineId: string;
  /** Display name */
  name: string;
  /** Site agent reachable in the last heartbeat window */
  agentOnline: boolean;
  /** ISO timestamp of last telemetry payload, or null */
  lastDataReceived: string | null;
  /**
   * `"Running" | "Idle" | "Stopped" | "Alarm" | "Offline" | null` — last
   * known status enum string, or null if no row yet.
   */
  status: string | null;
}

/** Tile semantic class — drives colour rules per §3.2.3 + §5.2. */
export type TileSemantic =
  | "good-true" // bool true means GOOD (e.g. cncReady, programRunning)
  | "info-true" // bool true is informational (coolantActive, mode flag when active)
  | "info-false" // bool false (most inactive bools)
  | "bad-when-true" // bool true is BAD (nckAlarmActive, coolantCoversBad)
  | "bad-when-false" // bool false is BAD (eStopOk, hydraulicsLevelOk, motorBreakersOk)
  | "number" // numeric tile
  | "string" // string tile
  | "placeholder"; // dashed-border placeholder (top alarm code, activeProgram)
